import { useState, useMemo } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from 'src/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { cn } from '@/lib/utils';

export interface AliasItem {
  id: string;
  alias: string;
  addedByUserId?: string | null;
  addedByUserName?: string | null;
  createdAt: string;
}

export interface AliasCrudTableProps {
  /** Title displayed above the table */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Array of alias items to display */
  aliases: AliasItem[];
  /** Loading state for the table */
  isLoading?: boolean;
  /** Callback when creating a new alias */
  onCreateAlias: (alias: string) => Promise<void>;
  /** Callback when updating an existing alias (optional - if not provided, edit is disabled) */
  onUpdateAlias?: (
    id: string,
    newAlias: string,
    oldAlias: string,
  ) => Promise<void>;
  /** Callback when deleting an alias */
  onDeleteAlias: (alias: AliasItem) => Promise<void>;
  /** Message shown when there are no aliases */
  emptyMessage?: string;
  /** Label for the alias field (defaults to "Alias") */
  aliasLabel?: string;
  /** Optional map of user IDs to user names for display */
  userNamesMap?: Map<string, string>;
  /** Additional actions to show in the header */
  headerActions?: React.ReactNode;
  /** Whether the table is in a compact mode */
  compact?: boolean;
  /** Custom class name for the container */
  className?: string;
  /** Format function for displaying the alias value (e.g., replacing '|||' with dots) */
  formatAlias?: (alias: string) => string;
  /** Parse function to transform user input back to storage format (e.g., dots back to '|||') */
  parseAlias?: (userInput: string) => string;
}

export function AliasCrudTable({
  title,
  description,
  aliases,
  isLoading = false,
  onCreateAlias,
  onUpdateAlias,
  onDeleteAlias,
  emptyMessage = 'No aliases found',
  aliasLabel = 'Alias',
  userNamesMap,
  headerActions,
  compact = false,
  className,
  formatAlias,
  parseAlias,
}: AliasCrudTableProps) {
  const [aliasModalOpen, setAliasModalOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState<AliasItem | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [aliasToDelete, setAliasToDelete] = useState<AliasItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newAliasValue, setNewAliasValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canEdit = !!onUpdateAlias;

  const handleOpenCreateModal = () => {
    setEditingAlias(null);
    setNewAliasValue('');
    setError(null);
    setAliasModalOpen(true);
  };

  const handleEditAlias = (alias: AliasItem) => {
    if (!canEdit) return;
    setEditingAlias(alias);
    // Show the formatted version in the input field for editing
    setNewAliasValue(formatAlias ? formatAlias(alias.alias) : alias.alias);
    setError(null);
    setAliasModalOpen(true);
  };

  const handleDeleteAlias = (alias: AliasItem) => {
    setAliasToDelete(alias);
    setDeleteConfirmOpen(true);
  };

  const handleModalClose = () => {
    setAliasModalOpen(false);
    setEditingAlias(null);
    setNewAliasValue('');
    setError(null);
  };

  const handleSaveAlias = async () => {
    const trimmedValue = newAliasValue.trim();
    if (!trimmedValue) {
      setError(`${aliasLabel} is required`);
      return;
    }

    // Parse the user input back to storage format (e.g., dots → '|||')
    const parsedValue = parseAlias ? parseAlias(trimmedValue) : trimmedValue;

    // Check for duplicates against the parsed (storage) format
    const isDuplicate = aliases.some(
      (a) =>
        a.alias.toLowerCase() === parsedValue.toLowerCase() &&
        a.id !== editingAlias?.id,
    );
    if (isDuplicate) {
      setError(`This ${aliasLabel.toLowerCase()} already exists`);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (editingAlias && onUpdateAlias) {
        await onUpdateAlias(editingAlias.id, parsedValue, editingAlias.alias);
      } else {
        await onCreateAlias(parsedValue);
      }
      handleModalClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An error occurred while saving',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDeleteAlias = async () => {
    if (!aliasToDelete) return;

    setIsDeleting(true);
    try {
      await onDeleteAlias(aliasToDelete);
      setDeleteConfirmOpen(false);
      setAliasToDelete(null);
    } catch (err) {
      console.error('Error deleting alias:', err);
      setDeleteConfirmOpen(false);
      setAliasToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const getUserName = (alias: AliasItem): string => {
    if (alias.addedByUserName) {
      return alias.addedByUserName;
    }
    if (alias.addedByUserId && userNamesMap?.has(alias.addedByUserId)) {
      return userNamesMap.get(alias.addedByUserId) || 'Unknown';
    }
    if (alias.addedByUserId) {
      return `User ${alias.addedByUserId.slice(0, 8)}...`;
    }
    return 'System';
  };

  const displayAlias = (alias: string): string => {
    if (formatAlias) {
      return formatAlias(alias);
    }
    return alias;
  };

  const formatDate = (dateString: string): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(dateString));
  };

  return (
    <>
      <div className={cn('space-y-4', className)}>
        <div className="flex justify-between items-center">
          <div>
            <h2 className={cn('font-medium', compact ? 'text-lg' : 'text-xl')}>
              {title}
              {aliases.length > 0 && (
                <span className="text-muted-foreground ml-2">
                  ({aliases.length})
                </span>
              )}
            </h2>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {headerActions}
            <Button
              onClick={handleOpenCreateModal}
              size="sm"
              className="flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add {aliasLabel}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <SpinnerIcon className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <span className="text-xs font-medium text-muted-foreground">
                      {aliasLabel}
                    </span>
                  </TableHead>
                  <TableHead>
                    <span className="text-xs font-medium text-muted-foreground">
                      Added By
                    </span>
                  </TableHead>
                  <TableHead>
                    <span className="text-xs font-medium text-muted-foreground">
                      Date
                    </span>
                  </TableHead>
                  <TableHead className="w-[100px]">
                    <span className="text-xs font-medium text-muted-foreground">
                      Actions
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aliases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <div className="text-center py-6">
                        <span className="text-sm text-muted-foreground">
                          {emptyMessage}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  aliases.map((alias) => (
                    <TableRow key={alias.id}>
                      <TableCell>
                        <span className="text-sm font-medium">
                          {displayAlias(alias.alias)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{getUserName(alias)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(alias.createdAt)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAlias(alias)}
                              className="h-8 w-8 p-0"
                              title={`Edit ${aliasLabel.toLowerCase()}`}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAlias(alias)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title={`Delete ${aliasLabel.toLowerCase()}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={aliasModalOpen} onOpenChange={handleModalClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAlias ? `Edit ${aliasLabel}` : `Add New ${aliasLabel}`}
            </DialogTitle>
            <DialogDescription>
              {editingAlias
                ? `Update the ${aliasLabel.toLowerCase()} value.`
                : `Add an alternative name or identifier.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="alias-value">{aliasLabel} *</Label>
              <Input
                id="alias-value"
                type="text"
                value={newAliasValue}
                onChange={(e) => {
                  setNewAliasValue(e.target.value);
                  setError(null);
                }}
                placeholder={`Enter ${aliasLabel.toLowerCase()}`}
                className={cn(error && 'border-red-500')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSaving) {
                    handleSaveAlias();
                  }
                }}
                autoFocus
              />
              {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAlias}
              disabled={!newAliasValue.trim() || isSaving}
            >
              {isSaving ? (
                <>
                  <SpinnerIcon className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : editingAlias ? (
                `Update ${aliasLabel}`
              ) : (
                `Add ${aliasLabel}`
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setAliasToDelete(null);
        }}
        onConfirm={confirmDeleteAlias}
        title={`Delete ${aliasLabel}`}
        message={`Are you sure you want to delete "${aliasToDelete ? displayAlias(aliasToDelete.alias) : ''}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={isDeleting}
        dangerous={true}
      />
    </>
  );
}

export default AliasCrudTable;
